import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import { Plus, X } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { Dropdown, NumberInput, TextArea } from "../../../common_components/FormInputs";
import { StyledCurrency } from "../../../helper/StyledCurrency";
import { formatStat } from "../../../helper/statsFormatter";
import { DEVELOPMENT_HOUR_TYPES } from "../billingSummary.constants";
/** Same geometry as the Generate Invoice modal's buttons — see that file's note on
 *  why `color="inherit"` is required to escape the global `containedPrimary` rule. */
const BUTTON_SX = {
    minWidth: 170,
    height: 36,
    minHeight: 36,
    px: "14px",
    borderRadius: "8px",
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap",
    boxShadow: "0px 1px 2px 0px #0A0D120D",
    gap: 1
};
/** Row ids only need to be unique within the open dialog. */
let rowSeq = 0;
const blankEntry = () => ({
    id: `dev_${(rowSeq += 1)}`,
    type: DEVELOPMENT_HOUR_TYPES[0].value,
    description: "",
    hours: "",
    rate: "",
});
/**
 * Whether the user actually filled this line in.
 *
 * `type` is NOT part of the test: a blank row opens with the first Type already
 * selected, so it says nothing about whether the line was touched. This mirrors the
 * blank-line rule the API applies in `sanitizeDevelopmentHours`, so what counts as a
 * line here is what counts as a line when it's stored.
 */
const isFilledEntry = (entry) => !!(entry.description.trim() || entry.hours.trim() || entry.rate.trim());
/** Hours × rate for one line; 0 until both are filled in. */
const lineTotal = (entry) => {
    const hours = parseFloat(entry.hours);
    const rate = parseFloat(entry.rate);
    if (isNaN(hours) || isNaN(rate))
        return 0;
    return hours * rate;
};
/**
 * "Development Hours" — the repeater opened from the Generate Invoice confirmation.
 *
 * Laid out in the dashboard's card-repeater idiom (the same one the Client
 * Contacts tab uses): a white card per line on a grey canvas, pink "Line #N"
 * heading, labelled fields, and an icon-action group carrying the remove button.
 * The "+" is a full-width add row at the bottom rather than a header icon, so the
 * next action always sits where the eye already is.
 */
const DevelopmentHoursModal = ({ open, clientName, onSave, onGenerateInvoice, onClose, loading = false, initialEntries, }) => {
    const [entries, setEntries] = useState([blankEntry()]);
    /**
     * Validation notice, shown in the dialog itself rather than as a toast: the dialog
     * is raised to `zIndex: 2000` (see its `sx`), which is above `ToastProvider`'s
     * `theme.zIndex.snackbar` layer, so a toast fired from here renders behind it.
     */
    const [error, setError] = useState(null);
    // Reset each time the dialog opens so a previous client's lines never leak in.
    useEffect(() => {
        if (!open)
            return;
        setEntries(initialEntries?.length ? initialEntries.map((e) => ({ ...e })) : [blankEntry()]);
        setError(null);
    }, [open, initialEntries]);
    const updateEntry = useCallback((id, field, value) => {
        setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));
        // Typing is the fix, so the notice clears as soon as the user starts.
        setError(null);
    }, []);
    const addEntry = useCallback(() => {
        setEntries((prev) => [...prev, blankEntry()]);
    }, []);
    // The first line is the required one, so it never offers a remove.
    const removeEntry = useCallback((id) => {
        setEntries((prev) => (prev.length > 1 ? prev.filter((entry) => entry.id !== id) : prev));
    }, []);
    const grandTotal = useMemo(() => entries.reduce((sum, entry) => sum + lineTotal(entry), 0), [entries]);
    // At least one real line is required to save. Checked on the click rather than by
    // disabling the button — see the note on `canSubmit` — so the user gets told what's
    // missing instead of facing a greyed-out control with no explanation.
    const handleSave = useCallback(() => {
        if (!entries.some(isFilledEntry)) {
            setError("Add at least one development hour line — fill in the description, hour(s) and rate before saving.");
            return;
        }
        setError(null);
        onSave(entries);
    }, [entries, onSave]);
    // Both actions stay live (and keep their colour) whenever we aren't mid-request.
    // Deliberately NOT gated on the lines being filled in: `AppButton` always carries
    // `app-button-grey-disabled`, whose `body …Mui-disabled` rule forces #f4f4f5, so a
    // validation gate here would grey both buttons out on open. Empty-line validation
    // belongs on the fields / a toast, not on the button's enabled state.
    const canSubmit = !loading;
    if (!open)
        return null;
    return (_jsx(Dialog, { open: open, maxWidth: "md", fullWidth: true, 
        // Same rule as the Generate Invoice dialog: only the explicit controls
        // dismiss it, so in-progress lines can't be lost to a stray click.
        disableEscapeKeyDown: true, sx: {
            zIndex: 2000,
            "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.5)",
                backdropFilter: "blur(3px)",
            },
            // Wider than the default so all four fields sit comfortably on one line.
            "& .MuiDialog-container > .MuiPaper-root": { borderRadius: "12px", maxWidth: "960px" },
        }, children: _jsxs(DialogContent, { sx: { p: 0 }, children: [_jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 3,
                        py: 2,
                        borderBottom: "1px solid #EAECF0",
                    }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "18px", fontWeight: 600, color: "#101828", lineHeight: "28px" }, children: "Development Hours" }), _jsx(Typography, { sx: { fontSize: "14px", color: "#667085", lineHeight: "20px", mt: 0.25 }, children: clientName
                                        ? `Add billable development hours for ${clientName}`
                                        : "Add billable development hours for this client" })] }), _jsx(IconButton, { onClick: onClose, disabled: loading, sx: { width: 36, height: 36, color: "#98A2B3", "&:hover": { color: "#475467", bgcolor: "#F9FAFB" } }, children: _jsx(X, { size: 20 }) })] }), _jsxs(Box, { sx: { bgcolor: "#F9FAFB", px: 3, py: 2, maxHeight: "62vh", overflowY: "auto" }, children: [_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: "10px" }, children: entries.map((entry, index) => {
                                const total = lineTotal(entry);
                                return (_jsxs(Box, { className: "contact-card-v2", sx: { p: "12px !important" }, children: [_jsxs(Box, { className: "contact-header-v2", children: [_jsx(Box, { className: "contact-title-wrap-v2" }), index > 0 && (_jsx(Box, { className: "contact-actions-v2", children: _jsx(HoverTooltip, { title: "Remove line", arrow: true, placement: "top", children: _jsx("button", { type: "button", className: "icon-action-btn-v2 danger", onClick: () => removeEntry(entry.id), disabled: loading, title: "Remove line", children: _jsx("img", { src: "/assets/trash.svg", alt: "", style: { width: 16, height: 16 } }) }) }) }))] }), _jsx(Box, { sx: { height: "1px", bgcolor: "#EAECF0", my: "10px" } }), _jsxs(Box, { sx: {
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.5fr 1.8fr 100px 110px" },
                                                gap: "10px 12px",
                                                alignItems: "start",
                                            }, children: [_jsx(Dropdown, { label: "Type", value: entry.type, onChange: (v) => updateEntry(entry.id, "type", Array.isArray(v) ? v[0] ?? "" : v), options: DEVELOPMENT_HOUR_TYPES, placeholder: "Select Type", size: "sm", sortByLabel: false, disabled: loading }), _jsx(TextArea, { label: "Description", value: entry.description, onChange: (e) => updateEntry(entry.id, "description", e.target.value), placeholder: "Add Description", minRows: 2, size: "sm", disabled: loading }), _jsx(NumberInput, { label: "Hour(s)", value: entry.hours, onChange: (e) => updateEntry(entry.id, "hours", e.target.value), placeholder: "Add Hour(s)", min: 0, size: "sm", disabled: loading }), _jsx(NumberInput, { label: "Rate", value: entry.rate, onChange: (e) => updateEntry(entry.id, "rate", e.target.value), placeholder: "Add Rate", min: 0, size: "sm", disabled: loading })] })] }, entry.id));
                            }) }), _jsx(AppButton, { variant: "outlined", color: "inherit", fullWidth: true, disabled: loading, onClick: addEntry, startIcon: _jsx(Plus, { size: 18 }), sx: {
                                mt: "10px",
                                height: 40,
                                borderRadius: "12px",
                                borderStyle: "dashed",
                                borderColor: "#D0D5DD",
                                bgcolor: "#fff",
                                color: "#FF008E",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "14px",
                                "&:hover": { borderStyle: "dashed", borderColor: "#FF008E", bgcolor: "#FFF5FA" },
                            }, children: "Add Another Developmet Hours" })] }), error && (_jsx(Box, { sx: { px: 3, pt: 2, bgcolor: "#fff" }, children: _jsx(Alert, { severity: "error", onClose: () => setError(null), sx: { borderRadius: "10px", alignItems: "center", fontSize: "13px" }, children: error }) })), _jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        px: 3,
                        py: 2,
                        borderTop: "1px solid #EAECF0",
                        bgcolor: "#fff",
                    }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: "13px", color: "#667085" }, children: "Total" }), _jsx(StyledCurrency, { value: formatStat(grandTotal, "amount"), fontSize: "16px", symbolSize: "13px", valueWeight: 600, symbolWeight: 400, valueColor: "#101828", symbolColor: "#A1A1AA" })] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(AppButton, { variant: "contained", color: "inherit", disabled: !canSubmit, onClick: handleSave, sx: {
                                        ...BUTTON_SX,
                                        minWidth: 100,
                                        bgcolor: "#FF008E",
                                        color: "#fff",
                                        border: "1px solid #FF008E",
                                        "&:hover": { bgcolor: "#D10075", border: "1px solid #D10075" }
                                    }, children: "Save" }), _jsx(AppButton, { variant: "contained", color: "inherit", disabled: !canSubmit, onClick: () => onGenerateInvoice(entries), sx: {
                                        ...BUTTON_SX,
                                        bgcolor: "#181D27",
                                        color: "#fff",
                                        border: "1px solid #181D27",
                                        "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                                    }, children: "Generate Invoice" })] })] })] }) }));
};
export default DevelopmentHoursModal;
